import { createTheme } from '@mui/material/styles';

/**
 * MUI theme, matched to design-system.css's tokens (src/styles/design-system.css). Without
 * this, MUI components (Snackbar, Alert, Chip, Dialog, spinners — used across ~20 files) fall
 * back to MUI's own default blue. Any page using MUI inherits this via the single
 * ThemeProvider in index.js — don't hardcode a color on an MUI component that could come from
 * here. Hex values mirror the CSS tokens because MUI needs real colors for its alpha math.
 */
const ink = '#26231f';
const border = '#e2ddd5';
const borderStrong = '#cfc8bc';
const fontStack = '"Geist", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const shadowLg = '0 12px 28px -8px rgb(31 28 24 / 0.14), 0 4px 8px -4px rgb(31 28 24 / 0.06)';
const shadowXl = '0 24px 56px -16px rgb(31 28 24 / 0.24), 0 8px 16px -8px rgb(31 28 24 / 0.08)';

const theme = createTheme({
  palette: {
    primary: { main: ink, dark: '#13110f', light: '#ebe8e3', contrastText: '#ffffff' },
    secondary: { main: '#a86a2a', dark: '#8f5920', light: '#f5ebdd', contrastText: '#ffffff' },
    success: { main: '#3b7550', light: '#e9f1eb', dark: '#2a573a' },
    warning: { main: '#9a6a1c', light: '#f8f0df', dark: '#6c4912' },
    error: { main: '#a5423a', light: '#f8e9e6', dark: '#7c2f28' },
    info: { main: '#45627a', light: '#eaeff3', dark: '#32485a' },
    background: { default: '#f6f4f0', paper: '#ffffff' },
    text: { primary: '#1f1c18', secondary: '#5b5448', disabled: '#a9a194' },
    divider: border,
    action: { hover: 'rgb(31 28 24 / 0.05)', selected: 'rgb(31 28 24 / 0.08)' },
  },
  typography: {
    fontFamily: fontStack,
    h1: { fontWeight: 600, letterSpacing: '-0.022em' },
    h2: { fontWeight: 600, letterSpacing: '-0.022em' },
    h3: { fontWeight: 600, letterSpacing: '-0.02em' },
    h4: { fontWeight: 600, letterSpacing: '-0.02em' },
    h5: { fontWeight: 600, letterSpacing: '-0.015em' },
    h6: { fontWeight: 600, letterSpacing: '-0.01em' },
    button: { textTransform: 'none', fontWeight: 500, letterSpacing: 0 },
    overline: { fontWeight: 600, letterSpacing: '0.06em' },
  },
  shape: { borderRadius: 7 },
  transitions: {
    easing: { easeOut: 'cubic-bezier(0.22, 1, 0.36, 1)' },
  },
  components: {
    MuiButtonBase: {
      defaultProps: { disableRipple: true },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          minHeight: 38,
          paddingInline: 16,
          transition: 'background-color 120ms, border-color 120ms, box-shadow 120ms, transform 120ms',
          '&:active': { transform: 'translateY(1px)' },
          '&.Mui-focusVisible': { boxShadow: '0 0 0 3px rgb(38 35 31 / 0.14)' },
        },
        outlined: {
          borderColor: borderStrong,
          backgroundColor: '#fff',
          '&:hover': { borderColor: '#a9a194', backgroundColor: '#faf9f7' },
        },
        sizeSmall: { minHeight: 30, paddingInline: 10 },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 7,
          '&:hover': { backgroundColor: 'rgb(31 28 24 / 0.06)' },
          '&.Mui-focusVisible': { boxShadow: '0 0 0 3px rgb(38 35 31 / 0.14)' },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: border },
        elevation1: { boxShadow: '0 1px 2px rgb(31 28 24 / 0.05), 0 0 0 1px ' + border },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { border: `1px solid ${border}`, borderRadius: 14 },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 14, border: `1px solid ${border}`, boxShadow: shadowXl },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          '&:not(.MuiBackdrop-invisible)': {
            backgroundColor: 'rgb(28 26 22 / 0.45)',
            backdropFilter: 'blur(3px)',
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { fontWeight: 600, fontSize: '1.125rem', letterSpacing: '-0.015em' } },
    },
    MuiMenu: {
      styleOverrides: {
        paper: { borderRadius: 9, border: `1px solid ${border}`, boxShadow: shadowLg, marginTop: 4 },
        list: { padding: 6 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: { borderRadius: 7, fontSize: '0.875rem', minHeight: 36 },
      },
    },
    MuiPopover: {
      styleOverrides: { paper: { borderRadius: 9, border: `1px solid ${border}`, boxShadow: shadowLg } },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: ink, fontSize: '0.75rem', fontWeight: 500, borderRadius: 6, padding: '6px 9px' },
        arrow: { color: ink },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: borderStrong },
          '&:hover:not(.Mui-focused):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': { borderColor: '#a9a194' },
          '&.Mui-focused': { boxShadow: '0 0 0 3px rgb(38 35 31 / 0.14)' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 1, borderColor: ink },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { fontSize: '0.875rem' } },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 6, fontWeight: 500, fontSize: '0.75rem' },
        sizeSmall: { height: 22 },
        outlined: { borderColor: border },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 9, fontSize: '0.875rem', alignItems: 'center' },
        standardSuccess: { backgroundColor: '#e9f1eb', color: '#2a573a', border: '1px solid #bfd7c6' },
        standardError: { backgroundColor: '#f8e9e6', color: '#7c2f28', border: '1px solid #e8c1bb' },
        standardWarning: { backgroundColor: '#f8f0df', color: '#6c4912', border: '1px solid #e7d3a8' },
        standardInfo: { backgroundColor: '#eaeff3', color: '#32485a', border: '1px solid #c5d2dc' },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: { root: { backgroundColor: ink, borderRadius: 9, boxShadow: shadowLg } },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: { '&.Mui-checked + .MuiSwitch-track': { opacity: 1 } },
        track: { backgroundColor: borderStrong, opacity: 1 },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 42, borderBottom: `1px solid ${border}` },
        indicator: { height: 2, backgroundColor: ink },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 42,
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          color: '#5b5448',
          '&.Mui-selected': { color: '#1f1c18' },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottomColor: '#eeebe5', fontSize: '0.875rem', fontVariantNumeric: 'tabular-nums' },
        head: {
          backgroundColor: '#efece6',
          color: '#5b5448',
          fontWeight: 600,
          fontSize: '0.6875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          borderBottomColor: border,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: { root: { borderRadius: 999, backgroundColor: '#e8e4dd' } },
    },
    MuiDrawer: {
      styleOverrides: { paper: { borderColor: border } },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: border } },
    },
  },
});

export default theme;
